import { project, SCENE_VIEW, type Camera, type ScreenPoint } from '../../engine/battlefield/projection';
import type { Token } from '../../engine/battlefield/tokens';
import type { Vec3 } from '../../engine/battlefield/types';
import type { FigureModel } from '../../engine/display/figureModel';
import { drawnRadius, figureHead, liesInCapsule, lyingCapsule } from '../../engine/display/figures';
import { headFaces, visibleHead, type HeadFace, type ShownFace } from '../../engine/display/heads';
import { closedPath } from '../../engine/display/svgPath';
import { boxFootprint } from '../../engine/plate/objectPlate';

/**
 * One display figure as the marker draws it (plan map-display, Track B): its
 * model, the head's visible faces (null for the living and the amorphous,
 * drawn as outlines) and, lying, the capsule on the ground.
 */
export interface FigureShape {
  model: FigureModel;
  head: ShownFace[] | null;
  capsule: string | null;
  /** A terrain Object's box footprint on the screen, corner by corner: its ring runs round it (board 6). */
  footprint: { x: number; y: number }[] | null;
}

/** A figure's geometry before any camera: where its head is, the head's faces, the lying capsule's ground. */
interface FigureWorld {
  head: Vec3;
  faces: HeadFace[] | null;
  capsule: Vec3[] | null;
  footprint: Vec3[] | null;
}

// Cached on the token, for its model: both keep their identity while only the
// camera moves, so an orbit re-projects without rebuilding (about a third of
// the figures' cost per frame, measured at 40 figures). A moved or re-modelled
// figure is a new token or a new model, and misses.
const worlds = new WeakMap<Token, { model: FigureModel; world: FigureWorld }>();

/** The figure's camera-free geometry (see `worlds`). */
export function figureWorld(token: Token, model: FigureModel): FigureWorld {
  const hit = worlds.get(token);
  if (hit?.model === model) return hit.world;
  const r = drawnRadius(token, model);
  const head = figureHead(token, model);
  const world = {
    head,
    // A crowd keeps its bodies (the marker's own crowd drawing): no single head.
    // An Object's diamond keeps its own size (its footprint), whatever ring its box gives it (2026-10-26).
    faces: model.crowd
      ? null
      : headFaces(model.klass, head, model.klass === 'object' ? token.footprint : r, token.heading ?? 0),
    capsule: liesInCapsule(model) ? lyingCapsule(token, r) : null,
    footprint: model.box ? boxFootprint(token, model.box) : null,
  };
  worlds.set(token, { model, world });
  return world;
}

/** The shape of one figure, seen through this camera; `at` places a point (the map's projection, or its marker's frame). */
export function figureShape(
  token: Token,
  model: FigureModel,
  camera: Camera,
  at: (p: Vec3) => ScreenPoint = (p) => project(p, camera, SCENE_VIEW),
): FigureShape {
  const { faces, capsule, footprint } = figureWorld(token, model);
  return {
    model,
    head: faces ? visibleHead(faces, camera, SCENE_VIEW, at) : null,
    capsule: capsule ? closedPath(capsule.map(at)) : null,
    footprint: footprint ? footprint.map(at).map(({ x, y }) => ({ x, y })) : null,
  };
}
